import { beginBrowserHydration } from "~/lib/ssr/browser-hydration";
import { StartClient } from "@tanstack/react-start/client";
import { hydrateRoot } from "react-dom/client";
import "~/lib/browser-buffer-polyfill";
import { StrictMode, startTransition } from "react";
import { overwriteGetLocale, overwriteSetLocale } from "~/lib/i18n";
import { preloadPrivyAppRuntimeProvider } from "~/components/lazy-privy-app-provider";
import { isExecutionStreamBrowserE2EMode } from "~/lib/e2e/is-execution-stream-browser-e2e-mode";
import { isMarketDataStreamBrowserE2EMode } from "~/lib/e2e/is-market-data-stream-browser-e2e-mode";
import { isWalletlessBrowserE2EMode } from "~/lib/e2e/is-walletless-browser-e2e-mode";
import { getPrivyAppId } from "~/lib/env";
import { installLocalPrivyApiProxy } from "~/lib/privy/local-privy-api-proxy";
import {
  isPublicBrowseInitialPathname,
  isStartRuntimeRequired,
  preloadInitialRouteModule,
} from "~/initial-route-preload";
import {
  hasPrivyOAuthCallback,
  hasPrivySessionHint,
} from "~/lib/auth/privy-session-hint";
import { getActiveLocale, setLanguageLocale } from "~/lib/language-lens";
import { installWebappV2ReleaseRecoveryHandlers } from "~/lib/release-recovery";
import { schedulePostPaint } from "~/lib/schedule-post-paint";
import { createSentryRootOptions } from "~/lib/sentry";

if (import.meta.env.DEV && window.location.hostname === "local.glider.fi") {
  installLocalPrivyApiProxy();
}

beginBrowserHydration(document.documentElement.lang);

overwriteGetLocale(() => getActiveLocale());
overwriteSetLocale((locale) => {
  setLanguageLocale(locale);
});

installWebappV2ReleaseRecoveryHandlers();

// Start known public browse graphs alongside provider/auth bootstrap. Routes
// remain code-split; this only removes the serial wait before their first
// above-the-fold render.
void preloadInitialRouteModule(window.location.pathname);

if (
  getPrivyAppId() !== null &&
  !window.location.pathname.startsWith("/design-system") &&
  (!isPublicBrowseInitialPathname(window.location.pathname) ||
    isStartRuntimeRequired(window.location.pathname, window.location.search) ||
    hasPrivySessionHint() ||
    hasPrivyOAuthCallback()) &&
  !isExecutionStreamBrowserE2EMode() &&
  !isMarketDataStreamBrowserE2EMode() &&
  !isWalletlessBrowserE2EMode()
) {
  // The mounted provider reports a required-load failure through the root
  // error boundary. This speculative caller must not create an orphan rejection.
  void preloadPrivyAppRuntimeProvider().catch(() => undefined);
}

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <StartClient />
    </StrictMode>,
    createSentryRootOptions(),
  );
});

schedulePostPaint(() => {
  void import("~/lib/public-browse-service-worker-client").then(
    ({ registerPublicBrowseServiceWorker }) =>
      registerPublicBrowseServiceWorker(),
  );
  void import("~/lib/release-version").then(
    ({ installReleaseVersionWatcher }) => installReleaseVersionWatcher(),
  );
}, 250);
